import { useEffect, useState } from "react";
import { peekResolvedFileUrl, resolveFileUrl } from "@/api/fileUrl";

/**
 * Resolves a company-scoped S3 url to a presigned GET url for display.
 * Renders the raw url immediately (stable while the bucket is still public),
 * then swaps to the resolved url once the exchange completes. Falls back to
 * the raw url if the exchange fails, with a single retry.
 */
export function useResolvedUrl(raw?: string | null): string | undefined {
  // Force a re-read of the S3 cache once the async exchange completes.
  const [, setNonce] = useState(0);
  const resolved = raw ? (peekResolvedFileUrl(raw) ?? raw) : undefined;

  useEffect(() => {
    if (!raw) return;

    let cancelled = false;
    let attempts = 0;

    const resolve = (): void => {
      resolveFileUrl(raw)
        .then(() => {
          if (!cancelled) setNonce((n) => n + 1);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempts < 1) {
            attempts += 1;
            window.setTimeout(resolve, 1500);
          }
        });
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [raw]);

  return resolved;
}
